import { Button, Dialog, Icon } from "armstrong-react";
import { Col, Grid, Row } from "armstrong-react-fix";
import React from "react";
import { connect } from "react-redux";
import Sticky from "react-stickynode";

import * as DateUtil from "@travellocal/utils/dateHelpers";
import { HeadingWithDescription } from "../../../../../core/atoms/atoms";
import { Loader } from "../../../../../core/atoms/loader";
import { ConfirmationDialog } from "../../../../../portal/components/controls/confirmationDialog";
import { NewTravelerDetailsForm } from "../../../../../shared/components/forms/newTravellerDetailsForm";
import { TravellerDetailsForm } from "../../../../../shared/components/forms/travellerDetailsForm";
import { CustomErrors } from "../../../../../shared/errors";
import { apiPromiseHook } from "../../../../../shared/flux/actions/enterHooks";
import * as EnquiryHelper from "../../../../../shared/helpers/enquiryHelpers";
import * as LayoutHelper from "../../../../../shared/helpers/layoutHelpers";
import { MatchState } from "../../../../../shared/helpers/reduxHelpers";
import * as StringUtil from "../../../../../shared/helpers/stringHelpers";
import { ValidationBar } from "../../../../../shared/validationBar";
import { ActionPromises } from "../../../../state/actionPromises";
import { EnterHookPromises } from "../../../../state/enterHookPromises";

import "./travellerDetails.scss";

interface StateProps {
  customerEnquiry: IState<IEnquiryOutput>;
}

interface DispatchProps {
  addNewTravelerDetails: (newTraveler: INewTravelerDetailsInput) => Promise<any>;
  updateTravelerDetails: (traveler: Partial<ITravelerDetailsUpdate>) => Promise<any>;
  updateLeadTravelerDetails: (enquiryId: number, traveler: Partial<ILeadTravelerDetailsUpdate>) => Promise<any>;
  deleteTravelerDetails: (travelerId: number) => Promise<any>;
  reloadEnquiry: (enquiryId: number) => Promise<any>;
}

type Props = DispatchProps & StateProps;

interface ICustomerTravellerDetailsComponentState {
  selectedTraveller?: ITravelerDetails | ILeadTravelerDetails;
  selectedTravellerErrors?: IValidationError[];
  addNewTravelerDialogOpen?: boolean;
  newTravelerErrors?: IValidationError[];
  isLeadTraveler?: boolean;
  canSubmit?: boolean;
  confirmDeleteDialogOpen?: boolean;
  isTravellerSelectorSticky?: boolean;
}

class CustomerTravellerDetailsComponent extends React.Component<Props, ICustomerTravellerDetailsComponentState> {
  private travellerSelectorSticky: Sticky;
  private deleteConfirmationRef: ConfirmationDialog;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLeadTraveler: false,
      selectedTraveller: null,
      addNewTravelerDialogOpen: false,
      newTravelerErrors: [],
      canSubmit: true,
      selectedTravellerErrors: [],
      confirmDeleteDialogOpen: false,
      isTravellerSelectorSticky: false,
    };
  }

  public createNewTravelerDetail(): INewTravelerDetailsInput {
    const enq = this.props.customerEnquiry.data;
    return {
      enquiryId: enq.id,
      firstName: "",
      lastName: "",
      title: null,
      otherTitle: null,
    };
  }

  public getTravellerDetail(travelerDetails: ITravelerDetails | ILeadTravelerDetails): ITravelerDetails {
    if (travelerDetails) {
      if (!travelerDetails.outboundFlight && this.state.isLeadTraveler) {
        travelerDetails.outboundFlight = { carrier: "", number: "", departureTime: null, arrivalTime: null };
      }
      if (!travelerDetails.returnFlight && this.state.isLeadTraveler) {
        travelerDetails.returnFlight = { carrier: "", number: "", departureTime: null, arrivalTime: null };
      }
      return travelerDetails as any;
    }
    return {
      id: 0,
      travelerNumber: 0,
      title: null,
      otherTitle: "",
      firstName: "",
      lastName: "",
      dateOfBirth: null,
      emailAddress: "",
      passportNumber: "",
      passportExpiryDate: null,
      passportIssuingCountry: null,
      countryOfResidence: null,
      dietaryRequirements: "",
      outboundFlight: { carrier: "", number: "", departureTime: null, arrivalTime: null },
      returnFlight: { carrier: "", number: "", departureTime: null, arrivalTime: null },
    };
  }

  public saveNewTraveler(t: INewTravelerDetailsInput) {
    const {
      addNewTravelerDetails,
      reloadEnquiry,
      customerEnquiry,
    } = this.props;

    addNewTravelerDetails(t).then(() => {
      this.setState({ addNewTravelerDialogOpen: false });
      reloadEnquiry(customerEnquiry.data.id).then(() => {
        this.setState({ selectedTraveller: customerEnquiry.data.travelerDetails[customerEnquiry.data.travelerDetails.length - 1], isLeadTraveler: false });
      });
    }).catch((err: Error) => {
      if (err instanceof CustomErrors.ApiError && err.response.status === 422) {
        this.setState({
          newTravelerErrors: err.validations || [{ message: err.text } as IValidationError],
        });
      }
      else {
        throw err;
      }
    });
  }

  public async updateTravelerDetails(traveler: ITravelerDetails | ILeadTravelerDetails) {
    const { isLeadTraveler } = this.state;
    const {
      updateLeadTravelerDetails,
      updateTravelerDetails,
      customerEnquiry,
    } = this.props;
    const { passportIssuingCountry, countryOfResidence, ...travelerDetails } = traveler;

    this.setState({ canSubmit: false, selectedTravellerErrors: [] });

    try {
      if (isLeadTraveler) {
        await updateLeadTravelerDetails(customerEnquiry.data.id, {
          ...travelerDetails as ILeadTravelerDetails,
          passportIssuingCountryId: passportIssuingCountry.id,
          countryOfResidenceId: countryOfResidence.id,
        } as Partial<ILeadTravelerDetailsUpdate>);
      } else {
        await updateTravelerDetails({
          ...travelerDetails as ITravelerDetails,
          passportIssuingCountryId: passportIssuingCountry.id,
        } as Partial<ITravelerDetailsUpdate>);
      }

      window.scrollTo(0, 0);
      this.setState({ canSubmit: true });
    }
    catch (e) {
      this.handlerSelectedTravelerErrors(e);
    }
  }

  public async deleteTraveler(id: number) {
    await this.props.deleteTravelerDetails(id);
    await this.props.reloadEnquiry(this.props.customerEnquiry.data.id);

    this.setState({
      confirmDeleteDialogOpen: false,
      selectedTraveller: this.props.customerEnquiry.data.leadTravelerDetails,
      isLeadTraveler: true,
    },
    );
  }

  public handlerSelectedTravelerErrors(err: Error) {
    window.scrollTo(0, 0);
    if (err instanceof CustomErrors.ApiError && err.response.status === 422) {
      this.setState({
        selectedTravellerErrors: err.validations || [{ message: err.text } as IValidationError],
        canSubmit: true,
      });
    }
    else {
      this.setState({ canSubmit: true });
      throw err;
    }
  }

  public getTravelerName(travelerDetails: ITravelerDetails): string {
    if (travelerDetails && travelerDetails.firstName && travelerDetails.lastName) {
      return `${travelerDetails.firstName[0].toUpperCase()}. ${travelerDetails.lastName}`;
    }
    return "Unnamed traveller";
  }

  public positionStickyElements = () => {
    if (!this.travellerSelectorSticky) {
      return;
    }

    if (this.travellerSelectorSticky.props.enabled && window.innerWidth <= LayoutHelper.breakpoints.tablet) {
      this.setState({ isTravellerSelectorSticky: false });
    }
    else if (!this.travellerSelectorSticky.props.enabled && window.innerWidth > LayoutHelper.breakpoints.tablet) {
      this.setState({ isTravellerSelectorSticky: true });
    }
  }

  public confirmDeleteAction(yesCallback: () => void) {
    this.setState({ confirmDeleteDialogOpen: true });
    this.deleteConfirmationRef.onConfirm = yesCallback;
  }

  public getTravelerMessage(): string {
    const enquiry = this.props.customerEnquiry.data;
    if (!enquiry.isConnected) {
      return null;
    }

    const travelersAdded = enquiry.travelerDetails.length + 1;
    const totalTravelers = enquiry.currentItinerarySummary.numberOfPeople;

    if (travelersAdded === totalTravelers) {
      return null;
    }

    return `There are ${travelersAdded} travellers listed on your enquiry, but your current itinerary quote is for ${totalTravelers}.`;
  }

  public buildTravellerSelector(traveller: ITravelerDetails | ILeadTravelerDetails): JSX.Element {
    const isLeadTraveller = !traveller.travelerNumber || traveller.travelerNumber <= 1;
    const isSelected = this.state.selectedTraveller && (this.state.selectedTraveller as ITravelerDetails).id === traveller.id;

    const selectTraveller = () => this.setState({ selectedTraveller: traveller, isLeadTraveler: isLeadTraveller, selectedTravellerErrors: [] });

    return <Grid key={`traveler_${traveller.id}`} className={`p-xsmall traveler-item ${isSelected ? "selected" : ""}`}>
      <Row>
        <Col onClick={selectTraveller}>
          <HeadingWithDescription titleText={`${this.getTravelerName(traveller)}`} description={isLeadTraveller ? "Lead Traveller" : "Traveller"} />
        </Col>
      </Row>
    </Grid>;
  }

  public componentDidMount() {
    window.addEventListener("resize", this.positionStickyElements);
    this.setState({ selectedTraveller: this.props.customerEnquiry.data.leadTravelerDetails, isLeadTraveler: true });
    this.positionStickyElements();
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.positionStickyElements);
  }

  private renderForm = () => {
    const enquiry = this.props.customerEnquiry.data;
    if (!enquiry.isConnected || !enquiry.currentItinerarySummary) {
      return;
    }

    const travellerMessage = this.getTravelerMessage();
    const travellersWithMissingFields = DateUtil.isBefore(DateUtil.now(), enquiry.currentItinerarySummary.startDate)
      ? EnquiryHelper.getTravellersWithMissingDetails(enquiry)
      : [];
    const leadTravellerDetails = this.getTravellerDetail(enquiry.leadTravelerDetails);

    return (
      <div className="customer-enquiry-traveller-details">
        <ConfirmationDialog
          message="Do you want to delete this traveller?"
          title="Are you sure?"
          ref={x => this.deleteConfirmationRef = x}
          isOpen={this.state.confirmDeleteDialogOpen}
          onClose={() => this.setState({ confirmDeleteDialogOpen: false })} />

        <Dialog title="Add new traveller"
          isOpen={this.state.addNewTravelerDialogOpen}
          onClose={() => this.setState({ addNewTravelerDialogOpen: false })}
          footerContent={(
            <Button
              leftIcon={Icon.Icomoon.checkmark}
              className="bg-positive"
              onClick={() => (this.refs["new-traveler-form"] as NewTravelerDetailsForm).save()}>
              Submit
            </Button>
          )}>
          {this.state.newTravelerErrors.length !== 0 && <ValidationBar errors={this.state.newTravelerErrors} />}
          <NewTravelerDetailsForm ref="new-traveler-form" data={this.createNewTravelerDetail()} save={(t) => this.saveNewTraveler(t)} />
        </Dialog>

        {this.props.customerEnquiry && travellersWithMissingFields.length > 0 &&
          <Grid className="reminder-banner">
            <Row className="width-wrapper">
              <Col width="1*">
                <p>
                  Please fill in the following information:
                    <ul>
                    {
                      travellersWithMissingFields.map((traveller, index) =>
                        <li key={`reminder-${index}`}>
                          {`${traveller.name} needs `}<strong>{StringUtil.arrayToSentence(traveller.fields)}</strong>
                        </li>)
                    }
                  </ul>
                </p>
              </Col>
              <Col width="auto" verticalAlignment="top">
                {/*TODO dismiss banner, for better mobile ux
                  <span className="icon icon-cross" onClick={_ => this.reminderBannerSticky.enabled = false}/>
                    */}
              </Col>
            </Row>
          </Grid>
        }

        <Grid className="width-wrapper rs-small-1col">
          <Row>
            <Col width={250} className="rs-small-unfix">
              <Sticky enabled={this.state.isTravellerSelectorSticky}
                top=".site-header"
                bottomBoundary="main"
                ref={x => this.travellerSelectorSticky = x}>
                <div className="traveller-details">
                  <div className="traveller-selectors">
                    {/* TODO collapse this into as dropdown on mobile */}
                    {this.buildTravellerSelector(leadTravellerDetails)}
                    {enquiry.travelerDetails.map(t => this.buildTravellerSelector(t))}
                    {travellerMessage &&
                      <p className="traveller-notice p-small">
                        <Icon icon={Icon.Icomoon.warning} className="m-right-xsmall" />
                        {travellerMessage}
                      </p>
                    }
                    <Button
                      leftIcon={Icon.Icomoon.plus2}
                      className="bg-positive m-top-small"
                      onClick={() => this.setState({ addNewTravelerDialogOpen: true })}>
                      Add another traveller
                    </Button>
                  </div>
                </div>
              </Sticky>
            </Col>
            {this.state.selectedTraveller &&
              <Col className="m-top-small">
                {!this.state.isLeadTraveler &&
                  <Button className="delete-button delete-traveller"
                    onClick={() => this.confirmDeleteAction(() => this.deleteTraveler(this.state.selectedTraveller.id))}
                    leftIcon={Icon.Icomoon.bin} >
                    Remove {this.getTravelerName(this.state.selectedTraveller)} from this trip
                  </Button>
                }
                <TravellerDetailsForm
                  ltoName={enquiry.lto.name}
                  errors={this.state.selectedTravellerErrors}
                  canSubmit={this.state.canSubmit}
                  isLeadTraveler={this.state.isLeadTraveler}
                  data={this.getTravellerDetail(this.state.selectedTraveller)}
                  save={(d) => this.updateTravelerDetails(d)}
                />
              </Col>
            }
          </Row>
        </Grid>
      </div>
    );
  }

  public render() {
    const { customerEnquiry } = this.props;

    return MatchState(customerEnquiry, {
      none: () => <Loader />,
      data: this.renderForm,
    }) as any;
  }
}

const stateToProps = (state: IWebStoreState): StateProps => {
  return {
    customerEnquiry: state.customerEnquiry,
  };
};

const dispatchToProps = (dispatch): DispatchProps => {
  return {
    addNewTravelerDetails: (newTravler: INewTravelerDetailsInput) => dispatch(ActionPromises.addNewTravelerDetails(newTravler)),
    updateTravelerDetails: (traveler: ITravelerDetails) => dispatch(ActionPromises.updateTravelerDetails(traveler)),
    updateLeadTravelerDetails: (enquiryId: number, traveler: ILeadTravelerDetails) => dispatch(ActionPromises.updateLeadTravelerDetails(enquiryId, traveler)),
    deleteTravelerDetails: (travelerId: number) => dispatch(ActionPromises.deleteTravelerDetails(travelerId)),
    reloadEnquiry: (enquiryId: number) => dispatch(ActionPromises.enquiryById(enquiryId)),
  };
};

export const TravellerDetails = connect(stateToProps, dispatchToProps)(CustomerTravellerDetailsComponent);

export const travellerDetailsOnEnter = apiPromiseHook<any>(
  EnterHookPromises.enquiryItineraryFromRoute(),
);
